import { layers as protomapsLayers, namedFlavor } from "@protomaps/basemaps";
import type { StyleSpecification } from "maplibre-gl";

export const BASEMAP_SOURCE = "basemap";
export const BASEMAP_URL = "pmtiles:///tiles/basemap.pmtiles";

/**
 * The base style: Protomaps' "light" flavor over our self-hosted Île-de-France
 * extract. Light mode is the default and the design baseline — the data layers
 * are colour-calibrated against this background.
 */
export function baseStyle(): StyleSpecification {
  return {
    version: 8,
    glyphs: "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf",
    sprite: "https://protomaps.github.io/basemaps-assets/sprites/v4/light",
    sources: {
      [BASEMAP_SOURCE]: {
        type: "vector",
        url: BASEMAP_URL,
        attribution:
          '<a href="https://openstreetmap.org">© OpenStreetMap</a> · <a href="https://protomaps.com">Protomaps</a>',
      },
    },
    layers: protomapsLayers(BASEMAP_SOURCE, namedFlavor("light"), { lang: "fr" }),
  };
}

/** The base style with label legibility tuned for data overlays. */
export function appStyle(): StyleSpecification {
  return strengthenLabels(baseStyle());
}

/**
 * Strengthen label halos.
 *
 * The Protomaps theme pairs grey text with a light grey halo, which is tuned
 * for a bare basemap. Once a saturated choropleth sits underneath, that halo
 * stops separating the text from the fill and street names get hard to read —
 * so labels get a solid white halo instead.
 */
export function strengthenLabels(style: StyleSpecification): StyleSpecification {
  for (const layer of style.layers) {
    if (layer.type !== "symbol") continue;
    const paint = (layer.paint ??= {});
    if (!("text-color" in paint)) continue;
    paint["text-halo-color"] = "#ffffff";
    paint["text-halo-width"] = 1.6;
    paint["text-halo-blur"] = 0.4;
  }
  return style;
}

/**
 * The id of the first symbol (label) layer. Data layers are inserted before it
 * so place names and street labels stay legible on top of choropleths.
 */
export function firstSymbolLayerId(style: StyleSpecification): string | undefined {
  return style.layers.find((l) => l.type === "symbol")?.id;
}
